"use client";

import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { ContactNumberProvider } from "@/hooks/contact-number-context/contact-number-context";
import fetchHelpOverlay from "@/hooks/magnolia/fetch-help-overlay";
import SessionFlagProvider from "@/providers/session-flag-provider";
import { RootState } from "@/store/root-reducer";
import { isHelpOverlayCallNumberBrandImpact } from "@/utils/help-overlay.utils";
import { getPhoneNumberByCountry } from "@/utils/origin-country-data";
import { getUtmSource, parsePartnerDataToArray } from "@/utils/utm.utils";

import ApplicationContextProvider, {
  AppConfig,
} from "./application-context-provider";
import BrandProvider from "./brand-provider";
import LoadingProvider from "./loading-provider";
import { NavigationProvider } from "./navigation-provider";

type HelpOverlayResponse = {
  ctaLabelOne?: string;
};

type PhoneNumberEntry = {
  desktop?: string;
  mobile?: string;
  name?: string;
  default?: string;
  "@path"?: string;
};

function AppProvider({
  children,
  appConfigData,
}: Readonly<{ children: React.ReactNode; appConfigData?: AppConfig | null }>) {
  const [phoneCustomerService, setPhoneCustomerService] = useState<string>("");

  // Access marketingChannelData from Redux store
  const marketingChannelData = useSelector(
    (state: RootState) =>
      state.quote?.attributes?.marketingChannelDetails?.marketingChannelData
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (isHelpOverlayCallNumberBrandImpact) {
          const exp: PhoneNumberEntry = (await getPhoneNumberByCountry()) ?? {};
          if (exp.default) {
            setPhoneCustomerService(exp.default);
          }
        } else {
          // Get utm_source from current URL or fallback to marketingChannelData
          const utmSource = getUtmSource(marketingChannelData);

          let pageJson: HelpOverlayResponse = {};
          pageJson = (await fetchHelpOverlay("highRisk")) ?? {};

          // Parse partner-specific phone numbers from CMS
          const partnerPhoneNumbers = parsePartnerDataToArray<{
            partnerKey: string;
            ctaLabel: string;
          }>((pageJson as any)?.partnerPhoneNumbers, "partnerPhoneNumbers");

          // Map partner phone number to ctaLabelOne if utm_source is available
          if (utmSource && partnerPhoneNumbers.length > 0) {
            const partnerData = partnerPhoneNumbers.find(
              (partner) => partner.partnerKey === utmSource
            );
            if (partnerData) {
              pageJson.ctaLabelOne = partnerData.ctaLabel;
            }
          }

          if (pageJson?.ctaLabelOne) {
            setPhoneCustomerService(pageJson.ctaLabelOne);
          }
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error fetching help overlay:", error);
      }
    };

    fetchData();
  }, [marketingChannelData]);

  return (
    <ContactNumberProvider phoneCustomerService={phoneCustomerService}>
      <LoadingProvider>
        <BrandProvider>
          <ApplicationContextProvider appConfig={appConfigData ?? null}>
            <NavigationProvider>
              <SessionFlagProvider>{children}</SessionFlagProvider>
            </NavigationProvider>
          </ApplicationContextProvider>
        </BrandProvider>
      </LoadingProvider>
    </ContactNumberProvider>
  );
}

export default AppProvider;
